import * as React from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export type ProjectItem = {
  id: string;
  name: string;
  subtitle?: string;
  status?: string; // allow your real statuses
  pinned?: boolean;
};

type Props = {
  scopeLabel?: string;              // e.g. "Projects"
  projects: ProjectItem[];
  activeProjectId?: string | null;

  // selection + actions
  onSelectProject?: (id: string) => void;
  onNewProject?: () => void;
  onViewAll?: () => void;

  // search integration
  value?: string;                   // controlled query (optional)
  onChange?: (q: string) => void;   // controlled query handler (optional)
  placeholder?: string;
};

function norm(s: string) {
  return s.trim().toLowerCase();
}

export default function ProjectsSearchBarDropdown({
  scopeLabel = 'Projects',
  projects,
  activeProjectId = null,
  onSelectProject,
  onNewProject,
  onViewAll,
  value,
  onChange,
  placeholder = 'Search…'
}: Props) {
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const [localQ, setLocalQ] = React.useState('');
  const q = value ?? localQ;

  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const setQ = (next: string) => {
    if (onChange) onChange(next);
    else setLocalQ(next);
  };

  const filtered = React.useMemo(() => {
    const nq = norm(q);
    if (!nq) return projects;
    return projects.filter((p) => norm(`${p.name} ${p.subtitle ?? ''} ${p.status ?? ''}`).includes(nq));
  }, [projects, q]);

  const pinned = React.useMemo(() => filtered.filter((p) => p.pinned), [filtered]);
  const rest = React.useMemo(() => filtered.filter((p) => !p.pinned), [filtered]);

  const handleOpen = () => setOpen(true);
  const handleToggle = () => setOpen((v) => !v);
  const handleClose = () => setOpen(false);

  const paperSx = {
    width: 420,
    borderRadius: 3,
    boxShadow: theme.shadows[10],
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    overflow: 'hidden'
  } as const;

  return (
    <Box ref={anchorRef} sx={{ width: '100%', maxWidth: 560 }}>
      <TextField
        fullWidth
        size="small"
        value={q}
        inputRef={inputRef}
        onFocus={() => {
          // optional: open dropdown on focus if you want it to behave like the screenshot
          // handleOpen();
        }}
        onChange={(e) => {
          setQ(e.target.value);
          if (!open) handleOpen(); // typing shows dropdown suggestions
        }}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <Button
                onClick={handleToggle}
                variant="text"
                color="inherit"
                endIcon={<KeyboardArrowDownRoundedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                  minWidth: 0,
                  borderRadius: 2,
                  color: 'text.primary',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                }}
              >
                {scopeLabel}
              </Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {q ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setQ('');
                    inputRef.current?.focus();
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              ) : (
                <SearchRoundedIcon fontSize="small" color="action" />
              )}
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.background.default, 0.55)
          }
        }}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } }
        ]}
        style={{ zIndex: theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={160}>
            <Paper sx={paperSx}>
              <ClickAwayListener
                onClickAway={(e) => {
                  // don’t close if click was inside the anchor input container
                  if (anchorRef.current && anchorRef.current.contains(e.target as Node)) return;
                  handleClose();
                }}
              >
                <Box>
                  {/* Header actions like screenshot */}
                  <Box sx={{ p: 1.25 }}>
                    <Button
                      fullWidth
                      onClick={() => {
                        onNewProject?.();
                        handleClose();
                      }}
                      startIcon={<AddRoundedIcon />}
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        py: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.65),
                        '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.8) }
                      }}
                    >
                      New Project
                    </Button>
                  </Box>

                  <Divider />

                  {/* Results */}
                  <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                    <List disablePadding>
                      <Typography
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 0.75,
                          color: 'text.secondary',
                          fontSize: 12,
                          fontWeight: 700
                        }}
                      >
                        Projects
                      </Typography>

                      {pinned.length > 0 &&
                        pinned.map((p) => {
                          const isActive = p.id === activeProjectId;
                          return (
                            <ListItemButton
                              key={p.id}
                              selected={isActive}
                              onClick={() => {
                                onSelectProject?.(p.id);
                                handleClose();
                              }}
                              sx={{
                                px: 1.5,
                                py: 1.05,
                                '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 34 }}>
                                <FolderRoundedIcon fontSize="small" color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontWeight: 650 }}>
                                      {p.name}
                                    </Typography>
                                    {p.status && (
                                      <Typography variant="caption" color="text.secondary">
                                        ({p.status})
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                                secondary={p.subtitle ? <Typography variant="caption">{p.subtitle}</Typography> : undefined}
                              />
                              {isActive && <CheckRoundedIcon fontSize="small" sx={{ color: 'primary.main' }} />}
                            </ListItemButton>
                          );
                        })}

                      {pinned.length > 0 && rest.length > 0 && <Divider sx={{ my: 0.75 }} />}

                      {rest.map((p) => {
                        const isActive = p.id === activeProjectId;
                        return (
                          <ListItemButton
                            key={p.id}
                            selected={isActive}
                            onClick={() => {
                              onSelectProject?.(p.id);
                              handleClose();
                            }}
                            sx={{
                              px: 1.5,
                              py: 1.05,
                              '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 34 }}>
                              <FolderRoundedIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontWeight: 500 }}>
                                    {p.name}
                                  </Typography>
                                  {p.status && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({p.status})
                                    </Typography>
                                  )}
                                </Stack>
                              }
                              secondary={p.subtitle ? <Typography variant="caption">{p.subtitle}</Typography> : undefined}
                            />
                            {isActive && <CheckRoundedIcon fontSize="small" sx={{ color: 'primary.main' }} />}
                          </ListItemButton>
                        );
                      })}

                      {filtered.length === 0 && (
                        <Box sx={{ px: 2, py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No projects found.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => {
                        onViewAll?.();
                        handleClose();
                      }}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        justifyContent: 'flex-start',
                        color: 'text.primary',
                        px: 1.5,
                        py: 1
                      }}
                    >
                      View All Projects
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}
